export type CheckoutAddressInput = {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  state?: string;
  countryCode: string;
  phone: string;
};

export type CheckoutValidationReason = "required" | "email" | "country" | "too_long" | "password";

export type CheckoutValidationIssue = {
  field: string;
  reason: CheckoutValidationReason;
  maximum?: number;
};

const LIMITS: Partial<Record<keyof CheckoutAddressInput, number>> = {
  email: 254,
  firstName: 100,
  lastName: 100,
  company: 160,
  addressLine1: 200,
  addressLine2: 200,
  postalCode: 32,
  city: 120,
  state: 120,
  countryCode: 2,
  phone: 40,
};

const REQUIRED_ADDRESS_FIELDS: readonly (keyof CheckoutAddressInput)[] = [
  "firstName",
  "lastName",
  "addressLine1",
  "postalCode",
  "city",
  "countryCode",
];

function normalized(value: string | undefined): string {
  return (value ?? "").normalize("NFKC").trim();
}

function validateAddress(address: CheckoutAddressInput, scope: "customer" | "shippingAddress", requireEmail: boolean) {
  const issues: CheckoutValidationIssue[] = [];
  const required = requireEmail ? (["email", ...REQUIRED_ADDRESS_FIELDS] as const) : REQUIRED_ADDRESS_FIELDS;
  for (const field of required) {
    if (!normalized(address[field])) issues.push({ field: `${scope}.${field}`, reason: "required" });
  }
  for (const [field, maximum] of Object.entries(LIMITS) as [keyof CheckoutAddressInput, number][]) {
    if (normalized(address[field]).length > maximum) {
      issues.push({ field: `${scope}.${field}`, reason: "too_long", maximum });
    }
  }
  const email = normalized(address.email);
  if (requireEmail && email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(email)) {
    issues.push({ field: `${scope}.email`, reason: "email" });
  }
  const country = normalized(address.countryCode);
  if (country && !/^[A-Za-z]{2}$/u.test(country)) {
    issues.push({ field: `${scope}.countryCode`, reason: "country" });
  }
  return issues;
}

export function validateCheckoutDetails(input: {
  billing: CheckoutAddressInput;
  shipping?: CheckoutAddressInput;
  createAccount: boolean;
  accountPassword: string;
  termsRequired: boolean;
  acceptTerms: boolean;
}): CheckoutValidationIssue[] {
  const issues = validateAddress(input.billing, "customer", true);
  if (input.shipping) issues.push(...validateAddress(input.shipping, "shippingAddress", false));
  if (input.createAccount) {
    const length = input.accountPassword.normalize("NFKC").length;
    if (length < 12 || length > 128) issues.push({ field: "account.password", reason: "password" });
  }
  if (input.termsRequired && !input.acceptTerms) issues.push({ field: "acceptTerms", reason: "required" });
  return issues;
}

export function checkoutFieldId(field?: string): string | undefined {
  if (!field) return undefined;
  if (field === "acceptTerms") return "checkout-acceptTerms";
  if (field === "digitalDeliveryConsent") return "checkout-digitalDeliveryConsent";
  if (field === "paymentMethod") return "checkout-paymentMethod";
  if (field === "password" || field === "account.password") return "checkout-account-password";
  const scoped = /^(customer|shippingAddress)\.(\w+)$/u.exec(field);
  if (scoped) return `${scoped[1] === "customer" ? "billing" : "shipping"}-${scoped[2]}`;
  if (/^(email|firstName|lastName|company|addressLine1|addressLine2|postalCode|city|state|countryCode|phone)$/u.test(field)) {
    return `billing-${field}`;
  }
  return undefined;
}
